import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ArrowRight,
  Timer,
  ShieldCheck,
  Paperclip,
  X,
  UserCheck,
  ClipboardList,
  MessageSquare,
  MessagesSquare,
  Flag,
} from 'lucide-react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { useAuth } from '@/features/auth';
import {
  useGateCheck,
  useTransitionMutation,
  useEmergencyMutations,
  useCompliance,
  useComplianceMutations,
  useDocumentMutations,
  useLeadMutations,
  useChangeRequestMutations,
  useMatterStatusMutations,
} from '../hooks';
import { InteractionDialog } from './InteractionDialog';
import { RISK_LEVEL_VALUES } from '../types';
import type {
  ComplianceStatus,
  GateBlock,
  Matter,
  MatterPhase,
  MatterResult,
  RiskLevel,
} from '../types';

interface GateTransitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matter: Matter | null;
  toPhase: MatterPhase | undefined;
  /**
   * Who executes the confirmed transition. 'transition' (default) calls the
   * generic phase FSM; 'emergencyResolve' calls the Stage 5 emergency resolve
   * (PROVISIONAL_OPEN → PLANNING) — the gate-check preview is Gate 1 either way.
   */
  mode?: 'transition' | 'emergencyResolve';
  onSuccess?: () => void;
}

/** Gate-1 inline compliance only offers the gate-passing decisions — FAILED
 *  stays in the full Compliance dialog (it could never pass the gate). */
const GATE_COMPLIANCE_OPTIONS: ComplianceStatus[] = [
  'PASSED',
  'PASSED_WITH_CONDITIONS',
  'BYPASSED',
];

const GATE1_COMPLIANCE_KEYS = ['compliance', 'riskLevel'];
const GATE2_PLAN_KEYS = ['matterPlan', 'estimate'];

/** Parse a non-negative integer from a text input ('' / invalid → undefined). */
function parseNonNegativeInt(value: string): number | undefined {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

function blockTone(block: GateBlock): {
  icon: React.ReactNode;
  rowClass: string;
} {
  if (block.status === 'pass') {
    return {
      icon: <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />,
      rowClass: 'border-emerald-500/30 bg-emerald-500/5',
    };
  }
  if (block.status === 'pending') {
    return {
      icon: <Clock className="h-4 w-4 text-slate-400 shrink-0" />,
      rowClass: 'border-slate-500/30 bg-slate-500/5',
    };
  }
  // fail
  if (block.severity === 'hard') {
    return {
      icon: <XCircle className="h-4 w-4 text-red-500 shrink-0" />,
      rowClass: 'border-red-500/40 bg-red-500/5',
    };
  }
  return {
    icon: <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />,
    rowClass: 'border-amber-500/40 bg-amber-500/5',
  };
}

export function GateTransitionDialog({
  open,
  onOpenChange,
  matter,
  toPhase,
  mode = 'transition',
  onSuccess,
}: GateTransitionDialogProps) {
  const { t } = useTranslation();
  const { hasAccess } = useAuth();
  const [reason, setReason] = React.useState('');

  const { data: check, isLoading } = useGateCheck(matter?.id, toPhase, open);
  const { transitionMutation } = useTransitionMutation();
  const { emergencyResolveMutation } = useEmergencyMutations();
  const { setComplianceMutation } = useComplianceMutations();
  const { createDocumentMutation } = useDocumentMutations();
  const { forceAssignMutation } = useLeadMutations();
  const { updatePlanMutation, confirmPlanMutation, requestFeedbackMutation } =
    useChangeRequestMutations();
  const { closeMutation } = useMatterStatusMutations();

  // ---- Gate 1 inline fields (compliance + risk + optional engagement letter) ----
  const isGate1 = check?.gate === 1;
  const complianceFailing =
    isGate1 &&
    (check?.blocks ?? []).some(
      (b) => GATE1_COMPLIANCE_KEYS.includes(b.key) && b.status === 'fail',
    );
  // The inline editor replaces the failing Compliance / Risk rows — only for
  // holders of matter:compliance (everyone else keeps the read-only rows).
  const showComplianceEditor =
    complianceFailing && hasAccess('matter', 'compliance');
  const letterBlock = (check?.blocks ?? []).find(
    (b) => b.key === 'engagementLetter',
  );
  // The optional upload replaces the informational engagement-letter row.
  const showLetterUpload =
    isGate1 &&
    !!letterBlock &&
    letterBlock.status !== 'pass' &&
    hasAccess('document', 'create');

  const { data: complianceState } = useCompliance(
    open && showComplianceEditor ? matter?.id : undefined,
  );

  // ---- Gate 2 inline fields (assign Lead + Matter Plan / estimate / budget) ----
  const isGate2 = check?.gate === 2;
  const leadFailing =
    isGate2 &&
    (check?.blocks ?? []).some(
      (b) => b.key === 'leadAssigned' && b.status === 'fail',
    );
  // Gate-2 transitions are Partner/MD-initiated, so the inline assignment is
  // the immediate force-assign (a pending delegation wouldn't pass the gate).
  const showLeadPicker = leadFailing && hasAccess('matter', 'delegate');
  const planFailing =
    isGate2 &&
    (check?.blocks ?? []).some(
      (b) => GATE2_PLAN_KEYS.includes(b.key) && b.status === 'fail',
    );
  const showPlanEditor = planFailing && hasAccess('matter', 'update');
  // The budget (billing cap) is in the contract's currency.
  const matterCurrency = matter?.contract?.currency ?? 'USD';

  // ---- Gate 3 — log the missing Interaction Log entry without leaving the gate ----
  const isGate3 = check?.gate === 3;
  const canLogInteraction = isGate3 && hasAccess('matter', 'interaction');
  const courtHearingFailing = (check?.blocks ?? []).some(
    (b) => b.key === 'courtHearing' && b.status === 'fail',
  );
  const [interactionDialogOpen, setInteractionDialogOpen] = React.useState(false);

  // ---- Gate 4 — mark Client Feedback as requested inline ----
  const isGate4 = check?.gate === 4;
  const feedbackFailing =
    isGate4 &&
    (check?.blocks ?? []).some(
      (b) => b.key === 'feedback' && b.status === 'fail',
    );
  // Gate-4 transitions are Partner/MD-initiated, matching the server's
  // Lead/Partner-or-above gate on the feedback-request endpoint.
  const showFeedbackToggle = feedbackFailing && hasAccess('matter', 'update');
  // Closing is never a bare phase flip — Gate 4 confirms through the close
  // endpoint, which requires a result (the server rejects transition→CLOSED).
  const isCloseGate = mode === 'transition' && toPhase === 'CLOSED';

  const [complianceStatus, setComplianceStatus] =
    React.useState<ComplianceStatus>('PASSED');
  const [riskLevel, setRiskLevel] = React.useState<RiskLevel>('LOW');
  const [bypassNote, setBypassNote] = React.useState('');
  const [letterFile, setLetterFile] = React.useState<File | null>(null);
  const letterInputRef = React.useRef<HTMLInputElement | null>(null);
  const [leadUserId, setLeadUserId] = React.useState<number | undefined>(undefined);
  const [scopeIn, setScopeIn] = React.useState('');
  const [scopeOut, setScopeOut] = React.useState('');
  const [estimate, setEstimate] = React.useState('');
  const [budget, setBudget] = React.useState('');
  const [requestFeedback, setRequestFeedback] = React.useState(true);
  const [result, setResult] = React.useState<MatterResult>('WIN');

  // Reset + seed once per dialog open. `matter` is read via a ref so a
  // background detail refetch (focus refetch) can't wipe in-progress input.
  const matterRef = React.useRef(matter);
  matterRef.current = matter;
  React.useEffect(() => {
    if (open) {
      const m = matterRef.current;
      setReason('');
      setComplianceStatus('PASSED');
      setRiskLevel('LOW');
      setBypassNote('');
      setLetterFile(null);
      setLeadUserId(undefined);
      // Pre-fill the plan fields from the matter so the gate edits, not wipes.
      setScopeIn(m?.scopeIn ?? '');
      setScopeOut(m?.scopeOut ?? '');
      setEstimate(m?.estimate != null ? String(m.estimate) : '');
      setBudget(m?.budget != null ? String(m.budget) : '');
      setRequestFeedback(true);
      setResult('WIN');
    }
  }, [open, toPhase]);

  // Pre-fill from the matter's current decision when it loads (defaults stay
  // PASSED / LOW so the gate can be cleared in one confirm). Seeded once per
  // open — a background refetch must not reset the user's picks.
  const complianceSeededRef = React.useRef(false);
  React.useEffect(() => {
    if (!open) complianceSeededRef.current = false;
  }, [open]);
  React.useEffect(() => {
    if (open && complianceState && !complianceSeededRef.current) {
      complianceSeededRef.current = true;
      if (GATE_COMPLIANCE_OPTIONS.includes(complianceState.compliance)) {
        setComplianceStatus(complianceState.compliance);
      }
      if (complianceState.riskLevel) {
        setRiskLevel(complianceState.riskLevel);
      }
    }
  }, [open, complianceState]);

  const bypass = complianceState?.bypass;
  const bypassBlocked =
    (bypass?.blocked ?? false) && complianceStatus === 'BYPASSED';
  const needsBypassNote =
    showComplianceEditor && complianceStatus === 'BYPASSED';
  const bypassNoteMissing = needsBypassNote && !bypassNote.trim();

  const hardFails = check?.hardFails ?? [];
  const softFails = check?.softFails ?? [];
  const openTimers = check?.openTimers ?? [];
  const estimateValue = parseNonNegativeInt(estimate);
  // Hard blocks the inline fields satisfy on confirm: Gate-1 Compliance / Risk
  // (saved first), Gate-2 Lead (force-assigned first, once one is picked).
  const effectiveHardFails = hardFails.filter((b) => {
    if (showComplianceEditor && GATE1_COMPLIANCE_KEYS.includes(b.key)) {
      return false;
    }
    if (showLeadPicker && b.key === 'leadAssigned' && leadUserId) return false;
    return true;
  });
  // Soft blocks the inline fields satisfy: the plan is saved + auto-confirmed
  // on confirm (the estimate check is informational — never a soft fail), and
  // Gate-4 feedback is marked as requested when the toggle stays checked.
  const effectiveSoftFails = softFails.filter((b) => {
    if (showPlanEditor && b.key === 'matterPlan') return false;
    if (showFeedbackToggle && b.key === 'feedback' && requestFeedback) {
      return false;
    }
    return true;
  });
  const canProceed = check ? effectiveHardFails.length === 0 : false;
  const needsReason = effectiveSoftFails.length > 0;
  const reasonMissing = needsReason && !reason.trim();

  // Rows the inline fields replace are not listed twice.
  const visibleBlocks = (check?.blocks ?? []).filter((block) => {
    if (showComplianceEditor && GATE1_COMPLIANCE_KEYS.includes(block.key)) {
      return false;
    }
    if (showLetterUpload && block.key === 'engagementLetter') return false;
    if (showLeadPicker && block.key === 'leadAssigned') return false;
    if (showPlanEditor && GATE2_PLAN_KEYS.includes(block.key)) return false;
    if (showFeedbackToggle && block.key === 'feedback') return false;
    return true;
  });

  const isPending =
    (mode === 'emergencyResolve'
      ? emergencyResolveMutation.isPending
      : transitionMutation.isPending) ||
    setComplianceMutation.isPending ||
    createDocumentMutation.isPending ||
    forceAssignMutation.isPending ||
    updatePlanMutation.isPending ||
    confirmPlanMutation.isPending ||
    requestFeedbackMutation.isPending ||
    closeMutation.isPending;

  const handleConfirm = async () => {
    if (!matter || !toPhase) return;
    try {
      // 1. Record the inline Compliance decision + Risk Level (Gate 1).
      if (showComplianceEditor) {
        await setComplianceMutation.mutateAsync({
          id: matter.id,
          data: {
            status: complianceStatus,
            riskLevel,
            ...(needsBypassNote ? { note: bypassNote.trim() } : {}),
          },
        });
      }
      // 2. Optional engagement letter upload (stored as a flagged document).
      if (showLetterUpload && letterFile) {
        await createDocumentMutation.mutateAsync({
          matterId: matter.id,
          file: letterFile,
          isEngagementLetter: true,
        });
      }
      // 3. Gate 2 — assign the Lead immediately (force-assign, no acknowledge).
      if (showLeadPicker && leadUserId) {
        await forceAssignMutation.mutateAsync({
          id: matter.id,
          toUserId: leadUserId,
        });
      }
      // 4. Gate 2 — save the Matter Plan fields, then auto-confirm the plan
      //    (confirming the transition is confirming the plan — Gate 2's soft
      //    check reads `matterPlanConfirmed`).
      if (showPlanEditor) {
        const budgetValue = parseNonNegativeInt(budget);
        const planData = {
          ...(scopeIn.trim() ? { scopeIn: scopeIn.trim() } : {}),
          ...(scopeOut.trim() ? { scopeOut: scopeOut.trim() } : {}),
          ...(estimateValue !== undefined ? { estimate: estimateValue } : {}),
          ...(budgetValue !== undefined ? { budget: budgetValue } : {}),
        };
        if (Object.keys(planData).length > 0) {
          await updatePlanMutation.mutateAsync({ id: matter.id, data: planData });
        }
        if (!matter.matterPlanConfirmed) {
          await confirmPlanMutation.mutateAsync(matter.id);
        }
      }
      // 5. Gate 4 — mark Client Feedback as requested (clears the soft check).
      if (showFeedbackToggle && requestFeedback) {
        await requestFeedbackMutation.mutateAsync(matter.id);
      }
      // 6. The transition itself — the server re-checks the gate live.
      if (mode === 'emergencyResolve') {
        // Stage 5 executor — the server re-enforces compliance + risk itself.
        await emergencyResolveMutation.mutateAsync({ id: matter.id });
      } else if (isCloseGate) {
        // Gate 4 — closing requires a result, so the close endpoint executes
        // (result + endedAt + KPI + success-fee billing live there).
        await closeMutation.mutateAsync({
          id: matter.id,
          result,
          ...(needsReason ? { softOverrideReason: reason.trim() } : {}),
        });
      } else {
        await transitionMutation.mutateAsync({
          id: matter.id,
          toPhase,
          softOverrideReason: needsReason ? reason.trim() : undefined,
        });
      }
    } catch {
      // Each mutation already surfaces its own error toast; whatever step
      // succeeded (compliance / letter) is saved and reflected on re-open.
      return;
    }
    onOpenChange(false);
    onSuccess?.();
  };

  const phaseLabel = (p?: MatterPhase | null) =>
    p ? t(`phase.${p}`) : '—';

  return (
    <>
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {t('gate.title')}
            {check?.gate ? (
              <span className="text-xs font-normal text-slate-500">
                {t('gate.gateN', { n: check.gate })}
              </span>
            ) : null}
          </AlertDialogTitle>
          <AlertDialogDescription className="flex items-center gap-2">
            <span className="font-medium">{phaseLabel(check?.fromPhase ?? matter?.phase)}</span>
            <ArrowRight className="h-4 w-4" />
            <span className="font-medium">{phaseLabel(toPhase)}</span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {isLoading ? (
          <div className="py-6 text-center text-sm text-slate-500">
            {t('gate.checking')}
          </div>
        ) : (
          <div className="space-y-3">
            <ScrollArea className="max-h-64 pr-3">
              <ul className="space-y-2">
                {visibleBlocks.map((block) => {
                  const tone = blockTone(block);
                  return (
                    <li
                      key={block.key}
                      className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${tone.rowClass}`}
                    >
                      {tone.icon}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span>{block.label}</span>
                          <span className="text-[10px] uppercase tracking-wide text-slate-400">
                            {t(`gate.severity.${block.severity}`)}
                          </span>
                        </div>
                        {block.note ? (
                          <p className="text-xs text-slate-400">{block.note}</p>
                        ) : null}
                        {block.key === 'interactionLog' &&
                        block.status === 'fail' &&
                        canLogInteraction ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2"
                            onClick={() => setInteractionDialogOpen(true)}
                          >
                            <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
                            {t('gate.logInteraction')}
                          </Button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
                {visibleBlocks.length === 0 &&
                !showComplianceEditor &&
                !showLetterUpload &&
                !showLeadPicker &&
                !showPlanEditor &&
                !showFeedbackToggle ? (
                  <li className="text-sm text-slate-500">{t('gate.noBlocks')}</li>
                ) : null}
              </ul>
            </ScrollArea>

            {/* Gate 1 — inline Compliance decision + Risk Level (saved on confirm) */}
            {showComplianceEditor ? (
              <div className="space-y-3 rounded-md border border-indigo-200 bg-indigo-50/40 px-3 py-3">
                <div className="flex items-center gap-2 text-sm font-medium text-indigo-700">
                  <ShieldCheck className="h-4 w-4" />
                  {t('gate.complianceSection')}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600">
                      {t('compliance.statusLabel')}
                    </label>
                    <Select
                      value={complianceStatus}
                      onValueChange={(v) =>
                        setComplianceStatus(v as ComplianceStatus)
                      }
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder={t('compliance.statusLabel')} />
                      </SelectTrigger>
                      <SelectContent>
                        {GATE_COMPLIANCE_OPTIONS.map((s) => (
                          <SelectItem
                            key={s}
                            value={s}
                            disabled={s === 'BYPASSED' && (bypass?.blocked ?? false)}
                          >
                            {t(`compliance.status.${s}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600">
                      {t('compliance.riskLabel')}
                    </label>
                    <Select
                      value={riskLevel}
                      onValueChange={(v) => setRiskLevel(v as RiskLevel)}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder={t('compliance.selectRisk')} />
                      </SelectTrigger>
                      <SelectContent>
                        {RISK_LEVEL_VALUES.map((r) => (
                          <SelectItem key={r} value={r}>
                            {t(`compliance.risk.${r}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {needsBypassNote ? (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600">
                      {t('compliance.noteLabel')}
                    </label>
                    <Textarea
                      value={bypassNote}
                      onChange={(e) => setBypassNote(e.target.value)}
                      placeholder={t('compliance.notePlaceholder')}
                      rows={2}
                      maxLength={1000}
                      className="bg-white"
                    />
                  </div>
                ) : null}
                {bypass && bypass.level !== 'ok' ? (
                  <p
                    className={`text-xs ${
                      bypass.level === 'red' ? 'text-red-600' : 'text-amber-600'
                    }`}
                  >
                    <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                    {t('compliance.bypassRate', {
                      rate: (bypass.rate * 100).toFixed(1),
                    })}
                  </p>
                ) : null}
                <p className="text-xs text-slate-500">
                  {t('gate.inlineComplianceHint')}
                </p>
              </div>
            ) : null}

            {/* Gate 1 — optional engagement letter upload */}
            {showLetterUpload ? (
              <div>
                <label className="mb-1 block text-sm font-medium">
                  {t('gate.engagementLetterLabel')}
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    ref={letterInputRef}
                    type="file"
                    onChange={(e) => setLetterFile(e.target.files?.[0] ?? null)}
                  />
                  {letterFile ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 shrink-0 p-0"
                      title={t('cancel')}
                      onClick={() => {
                        setLetterFile(null);
                        if (letterInputRef.current) {
                          letterInputRef.current.value = '';
                        }
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  ) : null}
                </div>
                <p className="mt-1 flex items-center gap-1 text-xs text-slate-400">
                  <Paperclip className="h-3 w-3" />
                  {t('gate.engagementLetterHint')}
                </p>
              </div>
            ) : null}

            {/* Gate 2 — assign the Lead inline (immediate force-assign) */}
            {showLeadPicker ? (
              <div className="space-y-3 rounded-md border border-indigo-200 bg-indigo-50/40 px-3 py-3">
                <div className="flex items-center gap-2 text-sm font-medium text-indigo-700">
                  <UserCheck className="h-4 w-4" />
                  {t('gate.leadSection')}
                </div>
                <UniversalCombobox
                  url="/users"
                  listUrl="/users/select-box"
                  labelKey="name"
                  valueKey="id"
                  selectedId={leadUserId}
                  params={{ status: 'ACTIVE', role_types: 'LEAD,PARTNER' }}
                  onSelectItem={(item) =>
                    setLeadUserId(item?.['id'] as number | undefined)
                  }
                  placeholder={t('leadDelegation.selectNewLead')}
                />
                <p className="text-xs text-slate-500">
                  {t('gate.leadInlineHint')}
                </p>
              </div>
            ) : null}

            {/* Gate 2 — Matter Plan / estimate / budget inline (saved + auto-confirmed) */}
            {showPlanEditor ? (
              <div className="space-y-3 rounded-md border border-indigo-200 bg-indigo-50/40 px-3 py-3">
                <div className="flex items-center gap-2 text-sm font-medium text-indigo-700">
                  <ClipboardList className="h-4 w-4" />
                  {t('plan.cardTitle')}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600">
                      {t('plan.scopeIn')}
                    </label>
                    <Textarea
                      value={scopeIn}
                      onChange={(e) => setScopeIn(e.target.value)}
                      rows={2}
                      maxLength={5000}
                      className="bg-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600">
                      {t('plan.scopeOut')}
                    </label>
                    <Textarea
                      value={scopeOut}
                      onChange={(e) => setScopeOut(e.target.value)}
                      rows={2}
                      maxLength={5000}
                      className="bg-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600">
                      {t('plan.estimate')} ({t('plan.hours')})
                    </label>
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      value={estimate}
                      onChange={(e) => setEstimate(e.target.value)}
                      className="bg-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600">
                      {t('plan.budget')} ({matterCurrency})
                    </label>
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      value={budget}
                      onChange={(e) => setBudget(e.target.value)}
                      className="bg-white"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  {t('gate.planInlineHint')}
                </p>
              </div>
            ) : null}

            {/* Gate 4 — Client Feedback requested (marked on confirm) */}
            {showFeedbackToggle ? (
              <div className="space-y-2 rounded-md border border-indigo-200 bg-indigo-50/40 px-3 py-3">
                <label className="flex cursor-pointer items-start gap-2">
                  <Checkbox
                    checked={requestFeedback}
                    onCheckedChange={(v) => setRequestFeedback(v === true)}
                    className="mt-0.5 bg-white"
                  />
                  <span className="min-w-0">
                    <span className="flex items-center gap-2 text-sm font-medium text-indigo-700">
                      <MessagesSquare className="h-4 w-4" />
                      {t('gate.feedbackLabel')}
                    </span>
                    <span className="block text-xs text-slate-500">
                      {t('gate.feedbackHint')}
                    </span>
                  </span>
                </label>
              </div>
            ) : null}

            {/* Gate 4 — closing requires a result (Win / Lose / …) */}
            {isCloseGate ? (
              <div className="space-y-2 rounded-md border border-indigo-200 bg-indigo-50/40 px-3 py-3">
                <div className="flex items-center gap-2 text-sm font-medium text-indigo-700">
                  <Flag className="h-4 w-4" />
                  {t('result.result')}
                </div>
                <Select
                  value={result}
                  onValueChange={(v) => setResult(v as MatterResult)}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder={t('selectResult')} />
                  </SelectTrigger>
                  <SelectContent>
                    {matter?.isCourtCase ? (
                      <>
                        <SelectItem value="WIN">{t('win')}</SelectItem>
                        <SelectItem value="LOSE">{t('lose')}</SelectItem>
                        <SelectItem value="SETTLED">{t('settled')}</SelectItem>
                        <SelectItem value="CANCELLED">{t('cancelled')}</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="WIN">{t('success')}</SelectItem>
                        <SelectItem value="LOSE">{t('failed')}</SelectItem>
                        <SelectItem value="CANCELLED">{t('cancelled')}</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {t('selectResultBeforeConfirming')}
                </p>
              </div>
            ) : null}

            {openTimers.length > 0 ? (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm">
                <div className="flex items-center gap-2 font-medium text-amber-600">
                  <Timer className="h-4 w-4" />
                  {t('gate.openTimersWarning', { count: openTimers.length })}
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {t('gate.openTimersHint')}
                </p>
              </div>
            ) : null}

            {needsReason ? (
              <div>
                <label className="mb-1 block text-sm font-medium">
                  {t('gate.overrideReasonLabel')}
                </label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('gate.overrideReasonPlaceholder')}
                  rows={3}
                />
              </div>
            ) : null}

            {effectiveHardFails.length > 0 ? (
              <p className="text-xs text-red-500">{t('gate.hardBlockedHint')}</p>
            ) : null}
          </div>
        )}

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              isLoading ||
              !canProceed ||
              reasonMissing ||
              bypassNoteMissing ||
              bypassBlocked ||
              isPending
            }
          >
            {isPending
              ? t('processing')
              : needsReason
                ? t('gate.proceedAnyway')
                : t('gate.confirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Gate 3 — log the missing interaction; the gate-check refreshes live.
        A failing court-hearing soft check pre-selects COURT_HEARING. */}
    <InteractionDialog
      open={interactionDialogOpen}
      onOpenChange={setInteractionDialogOpen}
      matter={matter}
      defaultType={courtHearingFailing ? 'COURT_HEARING' : 'MEETING'}
    />
    </>
  );
}
